/**
 * Voice-to-text dictation, hybrid across platforms.
 *
 * - On native (Capacitor iOS/Android) it uses the OS speech recognizer via
 *   `@capacitor-community/speech-recognition` (works inside the WKWebView where
 *   the Web Speech API is unavailable).
 * - On the web it falls back to the Web Speech API.
 *
 * Chrome/Safari stop a session after a pause. We restart until the user taps
 * stop, so long reflections aren't cut off mid-sentence.
 *
 * `onTranscript` receives the cumulative transcript for the current dictation
 * session; callers typically append it to a field's value-at-start.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { SpeechRecognition } from '@capacitor-community/speech-recognition';

const isNative = Capacitor.isNativePlatform();

interface WebSpeechEvent {
  results: ArrayLike<ArrayLike<{ transcript: string }>>;
}

interface WebSpeechErrorEvent {
  error?: string;
}

interface WebSpeechRecognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: WebSpeechEvent) => void) | null;
  onerror: ((event: WebSpeechErrorEvent) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

type WebSpeechCtor = new () => WebSpeechRecognition;

// Speech recognizers return spoken punctuation as words ("period", "comma").
// Convert them to real marks and tidy the spacing/casing around them so
// dictated text reads naturally without manual cleanup.
const SPOKEN_PUNCTUATION: Array<[RegExp, string]> = [
  [/\b(?:full stop|period)\b/gi, '.'],
  [/\bcomma\b/gi, ','],
  [/\bquestion mark\b/gi, '?'],
  [/\bexclamation (?:point|mark)\b/gi, '!'],
  [/\bsemicolon\b/gi, ';'],
  [/\bnew paragraph\b/gi, '\n\n'],
  [/\b(?:new line|newline)\b/gi, '\n'],
];

export function applySpokenPunctuation(raw: string): string {
  let text = raw;
  for (const [pattern, mark] of SPOKEN_PUNCTUATION) {
    text = text.replace(pattern, mark);
  }
  text = text
    .replace(/[ \t]+([.,!?;])/g, '$1')
    .replace(/([.,!?;])(?=[A-Za-z0-9])/g, '$1 ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n');
  // Capitalize the start of the dictation and of each new sentence/line.
  return text.replace(
    /(^|[.!?]\s+|\n\s*)([a-z])/g,
    (_match, before: string, letter: string) => before + letter.toUpperCase(),
  );
}

function getWebSpeechCtor(): WebSpeechCtor | null {
  const w = window as unknown as {
    SpeechRecognition?: WebSpeechCtor;
    webkitSpeechRecognition?: WebSpeechCtor;
  };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export interface UseDictation {
  /** Whether dictation is usable in this environment. */
  supported: boolean;
  /** Whether we're currently capturing speech. */
  listening: boolean;
  /** Start if idle, stop if listening. */
  toggle: () => void;
  /** Stop capturing. */
  stop: () => void;
}

export function useDictation(
  onTranscript: (sessionText: string) => void,
  language = 'en-US',
): UseDictation {
  const [supported, setSupported] = useState<boolean>(
    isNative ? true : !!getWebSpeechCtor(),
  );
  const [listening, setListening] = useState(false);
  const webRef = useRef<WebSpeechRecognition | null>(null);
  const wantRef = useRef(false);
  // Keep the latest callback without re-creating start/stop each render.
  const cbRef = useRef(onTranscript);
  useEffect(() => {
    cbRef.current = onTranscript;
  }, [onTranscript]);

  // Some devices/emulators report no recognizer; confirm before showing the mic.
  useEffect(() => {
    let active = true;
    if (isNative) {
      SpeechRecognition.available()
        .then((r) => { if (active) setSupported(!!r.available); })
        .catch(() => { if (active) setSupported(false); });
    }
    return () => { active = false; };
  }, []);

  const stop = useCallback(() => {
    wantRef.current = false;
    setListening(false);
    if (isNative) {
      SpeechRecognition.stop().catch(() => { /* already stopped */ });
      SpeechRecognition.removeAllListeners();
    } else if (webRef.current) {
      try { webRef.current.stop(); } catch { /* already stopped */ }
    }
  }, []);

  const start = useCallback(async () => {
    wantRef.current = true;
    if (isNative) {
      try {
        const perm = await SpeechRecognition.checkPermissions();
        if (perm.speechRecognition !== 'granted') {
          const req = await SpeechRecognition.requestPermissions();
          if (req.speechRecognition !== 'granted') {
            setSupported(false);
            wantRef.current = false;
            return;
          }
        }
        await SpeechRecognition.removeAllListeners();
        await SpeechRecognition.addListener('partialResults', (data: { matches?: string[] }) => {
          const text = data?.matches?.[0];
          if (text) cbRef.current(applySpokenPunctuation(text));
        });
        await SpeechRecognition.addListener(
          'listeningState',
          (data: { status?: string }) => {
            if (data?.status === 'stopped' && wantRef.current) {
              SpeechRecognition.start({ language, partialResults: true, popup: false })
                .catch(() => {
                  wantRef.current = false;
                  setListening(false);
                });
            }
          },
        );
        setListening(true);
        await SpeechRecognition.start({ language, partialResults: true, popup: false });
      } catch {
        wantRef.current = false;
        setListening(false);
      }
    } else {
      const Ctor = getWebSpeechCtor();
      if (!Ctor) {
        setSupported(false);
        wantRef.current = false;
        return;
      }
      const rec = new Ctor();
      rec.lang = language;
      rec.continuous = true;
      rec.interimResults = true;
      rec.onresult = (event) => {
        let text = '';
        for (let i = 0; i < event.results.length; i += 1) {
          text += event.results[i][0].transcript;
        }
        cbRef.current(applySpokenPunctuation(text));
      };
      rec.onerror = (event) => {
        if (event?.error === 'not-allowed' || event?.error === 'service-not-allowed') {
          wantRef.current = false;
          setListening(false);
          setSupported(false);
          return;
        }
        // no-speech / aborted / network: keep going if the user hasn't stopped.
      };
      rec.onend = () => {
        if (wantRef.current) {
          try {
            rec.start();
          } catch {
            wantRef.current = false;
            setListening(false);
          }
        } else {
          setListening(false);
        }
      };
      webRef.current = rec;
      setListening(true);
      try {
        rec.start();
      } catch {
        wantRef.current = false;
        setListening(false);
      }
    }
  }, [language]);

  const toggle = useCallback(() => {
    if (listening) stop();
    else start();
  }, [listening, start, stop]);

  // Stop cleanly if the component unmounts mid-session.
  useEffect(() => () => {
    wantRef.current = false;
    if (isNative) {
      SpeechRecognition.stop().catch(() => { /* noop */ });
      SpeechRecognition.removeAllListeners();
    } else if (webRef.current) {
      try { webRef.current.abort(); } catch { /* noop */ }
    }
  }, []);

  return { supported, listening, toggle, stop };
}
